import React from "react";
import Grid from "@material-ui/core/Grid";
import Avatar from "@material-ui/core/Avatar";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";

import { HoleSigns } from "types/hole";
import { useStyles } from "./styles/grossPicker";

interface GrossPickerProps {
  onSelect: (gross: HoleSigns | number | null) => void;
  dnfGrid?: boolean;
  embedded?: boolean;
  hideClear?: boolean;
}

const NUMBERS = [
  [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  [13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24],
  [25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36],
];

const GrossPicker: React.FC<GrossPickerProps> = ({ onSelect, dnfGrid, embedded, hideClear }) => {
  const classes = useStyles();
  const [index, setIndex] = React.useState<number>(0);
  const canGoForward = index < NUMBERS.length - 1;
  const pagerLabel = canGoForward ? (index === 0 ? "13+" : "25+") : "Back";
  const pagerAriaLabel = canGoForward
    ? index === 0
      ? "More scores, 13 and up"
      : "More scores, 25 and up"
    : "Back to scores 1 to 12";
  const onPagerClick = () => setIndex(prev => (prev < NUMBERS.length - 1 ? prev + 1 : 0));

  const numberKeys = NUMBERS[index].map((gross: number) => (
    <button
      type="button"
      key={gross}
      className={`${classes.embeddedKey} ${classes.embeddedKeyNumber}`}
      onClick={() => onSelect(gross)}
    >
      {gross}
    </button>
  ));

  const statusKeys = (
    <>
      <button type="button" className={`${classes.embeddedKey} ${classes.embeddedKeyStatus}`} onClick={() => onSelect(HoleSigns.PU)}>
        PU
      </button>
      {!dnfGrid && (
        <>
          <button type="button" className={`${classes.embeddedKey} ${classes.embeddedKeyStatus}`} onClick={() => onSelect(HoleSigns.DNF)}>
            DNF
          </button>
          <button type="button" className={`${classes.embeddedKey} ${classes.embeddedKeyStatus}`} onClick={() => onSelect(HoleSigns.DNP)}>
            DNP
          </button>
        </>
      )}
      <button
        type="button"
        className={`${classes.embeddedKey} ${classes.embeddedKeyPager}`}
        onClick={onPagerClick}
        aria-label={pagerAriaLabel}
      >
        {pagerLabel}
      </button>
    </>
  );

  if (embedded && !dnfGrid) {
    return (
      <div className={classes.embedded}>
        {hideClear ? null : (
          <button type="button" className={classes.embeddedClear} onClick={() => onSelect(null)}>
            CLEAR SCORE
          </button>
        )}
        <div className={classes.embeddedGrid}>
          {numberKeys}
          {statusKeys}
        </div>
      </div>
    );
  }

  return (
    <Grid container className={`${classes.root} ${dnfGrid ? "dnf" : ""}`}>
      <Grid item md={12}>
        {dnfGrid ? (
          <>
            <Typography variant="h6" style={{ marginBottom: 10 }}>
              Select number of shots played before retiring.
            </Typography>
            <Typography>All remaining holes will be marked as DNP. Use PU button for a general Pick Up.</Typography>
          </>
        ) : (
          <IconButton onClick={() => onSelect(null)}>
            <Avatar className={classes.clearButtonAvatar}>CLEAR SCORE</Avatar>
          </IconButton>
        )}
      </Grid>

      {NUMBERS[index].map((gross: number) => (
        <Grid item md={3} key={gross}>
          <IconButton onClick={() => onSelect(gross)}>
            <Avatar className={classes.avatar}>{gross}</Avatar>
          </IconButton>
        </Grid>
      ))}
      <Grid item md={3}>
        <IconButton onClick={() => onSelect(HoleSigns.PU)}>
          <Avatar className={classes.avatar2}>PU</Avatar>
        </IconButton>
      </Grid>
      {!dnfGrid && (
        <>
          <Grid item md={3}>
            <IconButton onClick={() => onSelect(HoleSigns.DNF)}>
              <Avatar className={classes.avatar2}>DNF</Avatar>
            </IconButton>
          </Grid>
          <Grid item md={3}>
            <IconButton onClick={() => onSelect(HoleSigns.DNP)}>
              <Avatar className={classes.avatar2}>DNP</Avatar>
            </IconButton>
          </Grid>
        </>
      )}
      <Grid item md={3}>
        <IconButton onClick={onPagerClick} aria-label={pagerAriaLabel}>
          <Avatar className={classes.pagerAvatar}>{pagerLabel}</Avatar>
        </IconButton>
      </Grid>
    </Grid>
  );
};

export default GrossPicker;
